/**
 * Analytics Utility
 * Handles Google Analytics and CloudWatch RUM event tracking
 * Respects cookie consent settings
 */

import { hasCookieConsent } from './cookieConsent'
import { trackPageView as trackRumPageView, trackCustomEvent as trackRumEvent, trackButtonClick as trackRumButtonClick } from './cloudwatchRum'

declare global {
  interface Window {
    gtag?: (...args: any[]) => void
    dataLayer?: any[]
  }
}

// Google Analytics Measurement ID - should be set via environment variable
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || ''

let isInitialized = false
let isScriptLoaded = false

/**
 * Initialize Google Analytics
 * Only loads if user has given cookie consent
 */
export function initializeAnalytics(): void {
  // Don't initialize if already initialized or no measurement ID
  if (isInitialized || !GA_MEASUREMENT_ID) {
    return
  }

  // Check cookie consent
  if (!hasCookieConsent()) {
    return
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || []
  window.gtag = function(...args: any[]) {
    window.dataLayer!.push(args)
  }

  // Load Google Analytics script
  if (!isScriptLoaded) {
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
    document.head.appendChild(script)

    // Configure GA
    window.gtag('js', new Date())
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: false // We'll handle page views manually
    })

    isScriptLoaded = true
  }

  isInitialized = true
}

/**
 * Track a page view
 * @param path - The page path
 * @param title - Optional page title
 */
export function trackPageView(path: string, title?: string): void {
  // Track in Google Analytics
  if (isInitialized && hasCookieConsent() && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: path,
      page_title: title || document.title
    })
  }

  // Track in CloudWatch RUM
  trackRumPageView(path)
}

/**
 * Track a custom event
 * @param eventName - Name of the event
 * @param eventParams - Optional event parameters
 */
export function trackEvent(eventName: string, eventParams?: Record<string, any>): void {
  // Track in Google Analytics
  if (isInitialized && hasCookieConsent() && window.gtag) {
    window.gtag('event', eventName, eventParams)
  }

  // Track in CloudWatch RUM
  trackRumEvent(eventName, eventParams)
}

/**
 * Track user login
 */
export function trackLogin(method: string = 'auth0'): void {
  trackEvent('login', { method })
}

/**
 * Track user signup
 */
export function trackSignup(method: string = 'auth0'): void {
  trackEvent('signup', { method })
}

/**
 * Track job import
 */
export function trackJobImport(jobTitle?: string, company?: string): void {
  trackEvent('job_import', {
    job_title: jobTitle,
    company: company
  })
  // Also track as button click for RUM
  trackRumButtonClick('ImportJob', { jobTitle, company })
}

/**
 * Track job state change
 */
export function trackJobStateChange(oldState: string, newState: string, jobTitle?: string): void {
  trackEvent('job_state_change', {
    old_state: oldState,
    new_state: newState,
    job_title: jobTitle
  })
}

/**
 * Track job deletion
 */
export function trackJobDeletion(jobTitle?: string, company?: string): void {
  trackEvent('job_delete', {
    job_title: jobTitle,
    company: company
  })
}

/**
 * Track interview preparation click
 */
export function trackInterviewPrep(jobTitle?: string, company?: string): void {
  trackEvent('interview_prep_click', {
    job_title: jobTitle,
    company: company
  })
}

/**
 * Track credit purchase (when implemented)
 */
export function trackCreditPurchase(amount: number, credits: number): void {
  trackEvent('purchase', {
    value: amount,
    currency: 'USD',
    credits: credits
  })
}

/**
 * Track feature usage
 */
export function trackFeatureUsage(featureName: string, additionalParams?: Record<string, any>): void {
  trackEvent('feature_usage', {
    feature_name: featureName,
    ...additionalParams
  })
}

/**
 * Re-initialize analytics if consent status changes
 * Call this when user accepts/rejects cookies
 */
export function reinitializeAnalytics(): void {
  if (hasCookieConsent()) {
    initializeAnalytics()
  } else {
    // If consent is revoked, we can't easily remove the script
    // but we can stop tracking by checking hasCookieConsent in each function
    isInitialized = false
  }
}

